import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/apiClient";
import { localizedPath, useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";
import NotFound from "@/pages/NotFound";

const KNOWN = new Set(["privacy", "terms", "refund", "disclaimer"]);

export default function LegalPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    setErr(null);
    if (!KNOWN.has(slug)) { setErr("not_found"); return; }
    api.get(`/public/legal/${slug}`).then(r => setPage(r.data)).catch((e) => {
      setErr(e?.response?.status === 404 ? "not_found" : "unavailable");
    });
  }, [slug]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [loc.pathname]);

  if (err === "not_found") return <NotFound />;
  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-muted-foreground">{lang === "id" ? "Halaman gagal dimuat. Coba lagi nanti." : "This page could not be loaded. Please try again later."}</p>
        <Link to="/" className="inline-block mt-4 text-terracotta-600 hover:underline" data-testid="legal-back-home">
          {lang === "id" ? "Kembali ke Beranda" : "Back to Home"}
        </Link>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="h-8 w-64 bg-secondary/60 animate-pulse rounded" />
        <div className="mt-4 h-64 bg-secondary/40 animate-pulse rounded" />
      </div>
    );
  }

  const title = lang === "id" ? page.title_id : (page.title_en || page.title_id);
  const body = lang === "id" ? page.body_id : (page.body_en || page.body_id);

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid={`legal-page-${slug}`}>
      <SEO slug={slug} title={title} body={body} lang={lang} />
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
        <Link to={localizedPath("/", lang)} className="hover:text-primary">{lang === "id" ? "Beranda" : "Home"}</Link>
        <span className="mx-2">/</span>
        <span>{lang === "id" ? "Legal" : "Legal"}</span>
      </div>
      <h1 className="font-display text-3xl md:text-4xl tracking-tight" data-testid="legal-page-title">{title}</h1>
      <article
        className="prose prose-teal max-w-none mt-6 text-teal-900/90 leading-relaxed [&_h3]:font-display [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1 [&_p]:my-3 break-words"
        data-testid="legal-page-body"
        dangerouslySetInnerHTML={{ __html: body || "" }}
      />
      <div className="mt-10 text-xs text-muted-foreground">
        {lang === "id" ? "Terakhir diperbarui: " : "Last updated: "}
        {page.updated_at ? new Date(page.updated_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { year: "numeric", month: "long", day: "numeric" }) : "—"}
      </div>
    </div>
  );
}

function SEO({ slug, title, body, lang }) {
  // Strip HTML tags for meta description (first 160 chars)
  const plain = (body || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const desc = plain.slice(0, 160);
  useSEO({
    title: `${title || "Legal"} — Mindovia`,
    description: desc || (lang === "id" ? "Halaman legal Mindovia." : "Mindovia legal page."),
    url: `/legal/${slug}`,
    image: "/og-cover.png",
  });
  return null;
}
